import React, { useState, useRef } from 'react';
import { ShoppingCart, BookOpen, Lock, CheckCircle, ExternalLink, ArrowRight, ArrowUpRight, Play, Pause, Volume2, Share2, Mail, Linkedin, Twitter, Link as LinkIcon, X, Headphones, Unlock, Send, Loader2, AlertCircle, Star, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEOMeta, { PAGE_META } from '../components/SEOMeta';
import { BookSchema, CourseSchema } from '../components/JsonLdSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Books = () => {
  const { hasModuleAccess, user } = useAuth();
  const [isModule1Open, setIsModule1Open] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const audioRef = useRef(null);

  const [bookEnquiry, setBookEnquiry] = useState({ name: '', email: '', phone: '', message: '' });
  const [bookEnquiryStatus, setBookEnquiryStatus] = useState('idle');
  const [bookEnquiryError, setBookEnquiryError] = useState('');

  const handleBookEnquiryChange = (e) => {
    setBookEnquiry({ ...bookEnquiry, [e.target.name]: e.target.value });
  };

  const handleBookEnquirySubmit = async (e) => {
    e.preventDefault();
    setBookEnquiryStatus('loading');
    setBookEnquiryError('');
    try {
      const res = await fetch(`${API_URL}/api/email/book-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookEnquiry),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send enquiry');
      }
      setBookEnquiryStatus('success');
    } catch (err) {
      setBookEnquiryStatus('error');
      setBookEnquiryError(err.message || 'Something went wrong.');
    }
  };

  const hasPremiumAccess = hasModuleAccess();

  const podcastUrl = 'https://customer-assets.emergentagent.com/job_db74d57d-6065-452a-a0c8-24e8297713b5/artifacts/e5jfnnn9_Cultural%20Fluency%20Without%20Compromise%20By%20Promise%20Monday%20Podcast%20mp3.MP3';
  const podcastShareUrl = `${window.location.origin}/books#podcast`;

  const modules = [
    { id: 1, title: 'Understanding Cultural Fluency', desc: 'Foundations of Third Culture Leadership and cultural intelligence.', duration: '45 mins', lessons: 5, isFree: true },
    { id: 2, title: 'Building Trust Across Cultures', desc: 'How trust manifests differently across cultures — strategies for authentic connection.', duration: '60 mins', lessons: 6, isFree: false },
    { id: 3, title: 'Leading with Cultural Clarity', desc: 'Navigate ambiguity and make clear decisions in multicultural environments.', duration: '55 mins', lessons: 5, isFree: false },
    { id: 4, title: 'Turning Diversity Into Advantage', desc: 'Harness diverse perspectives to drive innovation and competitive advantage.', duration: '50 mins', lessons: 5, isFree: false },
  ];

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.pause();
      else audioRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); };
  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) audioRef.current.currentTime = percent * duration;
  };
  const formatTime = (time) => `${Math.floor(time / 60)}:${Math.floor(time % 60).toString().padStart(2, '0')}`;

  const copyLink = () => { navigator.clipboard.writeText(podcastShareUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); };
  const shareOnLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(podcastShareUrl)}`, '_blank', 'width=600,height=600');
  const shareOnTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Listening to "Cultural Fluency Without Compromise" by Promise Monday. Essential insights for cross-cultural leadership!')}&url=${encodeURIComponent(podcastShareUrl)}&hashtags=CulturalFluency,Leadership`, '_blank', 'width=600,height=400');
  const shareViaEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent('Cultural Fluency Without Compromise Podcast')}&body=${encodeURIComponent('Listen to this podcast by Promise Monday:\n\n' + podcastShareUrl)}`; };

  const testimonials = [
    { quote: "An eye-opener and a liberator... a must-read.", name: "Dr Taiwo Maraiyesa", role: "Consultant Paediatrician, NHS England" },
    { quote: "A power-packed book... must-read for all immigrants.", name: "Dr Mike O. Atoe-Imagbe", role: "Consultant in Medicine & Cardiology, NHS" },
    { quote: "Fosters team cohesion... an amazing must-read.", name: "Dr Mirabel Osayi", role: "Portfolio General Practitioner, NHS" },
    { quote: "Gives language to what many experience but cannot easily name.", name: "Ngozi Iseh-Aghahowa, MD", role: "Paediatric Hospitalist, USA" },
    { quote: "A manual for any professional practising across cultures. A third culture is possible!", name: "Oghenevo Efekodo", role: "Founder, Nigerian Doctors in the UK (NDUK)" },
    { quote: "A well thought through and well written book.", name: "Mr Muyiwa Owoniyi", role: "Consultant Ophthalmologist, NHS" },
    { quote: "Tackles an important, overlooked aspect of working in the NHS.", name: "Dr S Kumar Sethuraman", role: "Consultant Paediatrician, NHS" },
    { quote: "Deeply reflective and thought provoking.", name: "Dr Ejiro Ogonor", role: "Paediatrician, NHS England" },
  ];

  return (
    <div className="bg-[#FAF8F3]" data-testid="books-page">
      <SEOMeta url="/books" {...PAGE_META.books} />
      <BookSchema />
      <CourseSchema />

      {/* ═══ HERO — Book Showcase (dark for drama) ═══ */}
      <section className="relative pt-32 pb-20 lg:pb-28 overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
        {/* Decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-[#C99316]/[0.03] rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C99316]/60 font-sans font-medium mb-4 text-center lg:text-left">
            Featured Publication
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Book Cover */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative group" data-testid="book-cover-showcase">
                <div className="absolute -inset-6 bg-[#C99316]/[0.06] blur-3xl" />
                <img
                  src="https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg"
                  alt="Cultural Fluency Without Compromise book cover"
                  className="relative w-[260px] sm:w-[300px] shadow-2xl shadow-black/40 transform -rotate-1 group-hover:rotate-0 group-hover:scale-[1.02] transition-all duration-500"
                  data-testid="book-cover-image"
                />
              </div>
            </div>

            {/* Book Details */}
            <div data-testid="book-details">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[0.95] mb-3 font-light tracking-tighter">
                Cultural Fluency<br />
                <span className="text-gold">Without Compromise</span>
              </h1>
              <p className="text-cream/72 text-base mb-2 tracking-wide font-light">
                Thriving Across Cultures with Presence and Integrity
              </p>
              <p className="text-cream/50 text-sm mb-6 tracking-wide">
                By <span className="text-cream/75">Promise Monday</span> &bull; Promond Publishing
              </p>
              <p className="text-cream/75 font-light leading-[1.8] mb-8 max-w-md tracking-wide text-base">
                A practical framework for leading across cultures without losing identity. Navigate cultural complexity with confidence and create environments where diverse teams thrive.
              </p>

              {/* Pull quote */}
              <div className="border-l-2 border-gold/40 pl-5 mb-8">
                <p className="text-cream/70 font-serif italic text-lg leading-relaxed">
                  "A third space is possible — where leaders build bridges without blurring values."
                </p>
                <p className="text-gold/50 text-xs mt-2 uppercase tracking-wider">— Promise Monday</p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/books/cultural-fluency-without-compromise/read"
                  data-testid="read-online-btn"
                  className="btn-editorial border border-gold text-gold px-7 py-3.5 text-[12px] uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2"
                >
                  <BookOpen size={16} />
                  <span>Read Online</span>
                </Link>
                <Link
                  to="/books/cultural-fluency-without-compromise/links"
                  className="border-b border-cream/20 pb-1 text-[12px] uppercase tracking-[0.12em] text-cream/50 font-sans hover:border-gold hover:text-gold transition-all duration-500 inline-flex items-center gap-2"
                >
                  <ExternalLink size={14} />
                  Buy Links
                </Link>
                <a
                  href="https://books.by/promond-publishing/cultural-fluency-without-com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-cream/15 text-cream/70 font-sans text-sm font-medium rounded-full hover:border-gold/40 hover:text-gold transition-all duration-300"
                >
                  <ShoppingCart size={16} />
                  Paperback
                </a>
              </div>

              {/* Retailers */}
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="text-cream/50 text-xs">Also on:</span>
                <a href="https://www.amazon.co.uk/dp/1918265003" target="_blank" rel="noopener noreferrer" className="text-[11px] px-3 py-1 bg-cream/8 text-cream/60 border border-cream/10 hover:bg-gold/10 hover:text-gold transition-all">Amazon UK</a>
                <a href="https://a.co/d/iAG3RiE" target="_blank" rel="noopener noreferrer" className="text-[11px] px-3 py-1 bg-cream/8 text-cream/60 border border-cream/10 hover:bg-gold/10 hover:text-gold transition-all">Amazon Global</a>
                <a href="https://books.apple.com/gb/book/cultural-fluency-without-compromise/id6755616279" target="_blank" rel="noopener noreferrer" className="text-[11px] px-3 py-1 bg-cream/8 text-cream/60 border border-cream/10 hover:bg-gold/10 hover:text-gold transition-all">Apple Books</a>
              </div>
            </div>
          </div>
        </div>

        {/* Gold separator */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ═══ KEY TOPICS ═══ */}
      <section className="bg-cream py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {['Cultural Intelligence', 'Leadership', 'Cross-Cultural Communication', 'Identity', 'Team Building', 'Global Excellence'].map((topic) => (
              <span key={topic} className="px-4 py-2 bg-gold/[0.08] border border-gold/15 text-gold/80 text-xs uppercase tracking-wider rounded-full">
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="bg-[#EDE9E0] py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute top-8 left-1/2 -translate-x-1/2 text-[180px] font-serif text-gold/[0.05] leading-none select-none pointer-events-none" aria-hidden="true">&ldquo;</div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="text-center mb-14">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 font-sans mb-4">Praise</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy font-semibold">What Leaders Are Saying</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="group bg-white border border-gold/15 rounded-xl p-6 hover:border-gold/30 hover:shadow-md hover:shadow-navy/[0.06] transition-all duration-300 shadow-sm shadow-navy/[0.03]"
                data-testid={`testimonial-${i}`}
              >
                <Quote size={16} className="text-gold/50 mb-3" />
                <p className="text-navy/60 text-sm leading-relaxed italic mb-4">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="border-t border-gold/[0.1] pt-3">
                  <p className="text-navy text-sm font-medium">{t.name}</p>
                  <p className="text-navy/35 text-xs mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ═══ APPLE BOOKS REVIEW ═══ */}
      <section className="bg-cream py-20 relative overflow-hidden">
        <div className="absolute top-8 left-1/2 -translate-x-1/2 text-[180px] font-serif text-gold/[0.04] leading-none select-none pointer-events-none" aria-hidden="true">&ldquo;</div>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="text-[11px] uppercase tracking-[0.3em] text-gold/60 font-sans">Apple Books Review</span>
            <div className="flex gap-0.5">{[1,2,3,4,5].map((s) => <Star key={s} size={12} className="text-gold fill-gold" />)}</div>
          </div>
          <div className="bg-white border border-gold/15 p-8 sm:p-10 shadow-sm shadow-navy/[0.04] rounded-xl" data-testid="apple-books-review">
            <h3 className="font-serif text-2xl sm:text-3xl text-navy font-light tracking-tight mb-6 text-center italic">&ldquo;A wonderful book&rdquo;</h3>
            <div className="space-y-4 text-navy/55 text-sm sm:text-[15px] font-light leading-[1.9] tracking-wide">
              <p>What I particularly appreciated was how the book slows the one down and invites honest self-reflection — helping one locate themselves within the cultural matrix while learning how to bridge differences without losing their own voice. It addresses a very real but often unspoken area of non-clarity, especially in diverse professional environments, and does so with grace and depth.</p>
              <p>I truly believe this is the kind of work that should be incorporated at organisational and NHS team-planning levels, as it broadens perspective and encourages healthier, more conscious interactions within teams.</p>
              <p>Overall, this is a beautiful initiative and a meaningful contribution. I thoroughly enjoyed reading it and appreciate the thought, care, and insight that went into creating it.</p>
              <p className="text-navy/35 italic">Thank you for such important work.</p>
            </div>
            <div className="mt-8 pt-6 border-t border-gold/[0.1] flex items-center justify-between">
              <div>
                <p className="text-navy/70 text-sm font-medium tracking-wide">Akpevwe Ofovwe</p>
                <p className="text-navy/25 text-xs tracking-wide">22 January 2026</p>
              </div>
              <a href="https://books.apple.com/gb/book/cultural-fluency-without-compromise/id6755616279" target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-[0.15em] text-gold/60 hover:text-gold transition-colors font-sans flex items-center gap-1.5">
                View on Apple Books <ArrowUpRight size={10} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ AMAZON REVIEWS ═══ */}
      <section className="bg-[#EDE9E0] py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold/60 font-sans mb-8 text-center">Amazon Reader Reviews</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              "Finally, language for what I've experienced but couldn't name. This book changed how I lead.",
              "A manual for any professional practising across cultures. Highly recommended.",
              "Essential reading for leaders navigating multicultural teams. Practical and profound.",
            ].map((review, i) => (
              <div key={i} className="bg-white border border-navy/[0.07] rounded-xl p-5 text-center shadow-sm shadow-navy/[0.03]">
                <div className="flex justify-center gap-0.5 mb-3">
                  {[1,2,3,4,5].map((s) => <Star key={s} size={14} className="text-gold fill-gold" />)}
                </div>
                <p className="text-navy/60 italic text-base leading-relaxed">&ldquo;{review}&rdquo;</p>
                <p className="text-navy/25 text-xs mt-3">Verified Reader</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BUY LINKS BAND ═══ */}
      <section className="bg-cream py-14">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h3 className="font-serif text-2xl text-navy text-center mb-8 font-semibold">Get Your Copy</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Link
              to="/books/cultural-fluency-without-compromise/read"
              data-testid="buy-section-read-online"
              className="flex items-center justify-center gap-2 bg-gold text-navy px-4 py-3.5 rounded-xl text-sm font-semibold hover:bg-gold-light transition-colors"
            >
              <BookOpen size={16} /> Read Online
            </Link>
            {[
              { label: 'Books.by', href: 'https://books.by/promond-publishing/cultural-fluency-without-com' },
              { label: 'Apple Books', href: 'https://books.apple.com/gb/book/cultural-fluency-without-compromise/id6755616279' },
              { label: 'Amazon UK', href: 'https://www.amazon.co.uk/dp/1918265003' },
              { label: 'Amazon Global', href: 'https://a.co/d/iAG3RiE' },
            ].map((link) => (
              <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-navy/[0.05] border border-navy/10 text-navy/60 px-4 py-3.5 rounded-xl text-sm font-medium hover:bg-gold/10 hover:border-gold/25 hover:text-gold transition-all">
                {link.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-14 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ═══ PODCAST ═══ */}
      <section id="podcast" className="bg-cream py-20" data-testid="podcast-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Art */}
            <div className="w-44 h-44 md:w-52 md:h-52 flex-shrink-0 rounded-xl overflow-hidden shadow-lg shadow-navy/15 ring-1 ring-gold/15">
              <img
                src="https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg"
                alt="Podcast cover"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 w-full">
              <div className="flex items-center gap-2 mb-2">
                <Headphones size={16} className="text-gold/70" />
                <span className="text-gold/70 text-[11px] font-sans uppercase tracking-[0.2em]">Podcast</span>
              </div>
              <h3 className="font-serif text-2xl text-navy font-semibold mb-1">Cultural Fluency Without Compromise</h3>
              <p className="text-navy/35 text-sm mb-5">By Promise Monday</p>

              <audio ref={audioRef} src={podcastUrl} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} />

              <div className="bg-navy/[0.04] border border-navy/[0.08] rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <button onClick={togglePlay} className="w-12 h-12 flex-shrink-0 bg-gold rounded-full flex items-center justify-center hover:bg-gold-light transition-colors shadow-lg shadow-gold/20" aria-label={isPlaying ? 'Pause' : 'Play'} data-testid="podcast-play-btn">
                    {isPlaying ? <Pause size={20} className="text-navy" fill="currentColor" /> : <Play size={20} className="text-navy ml-0.5" fill="currentColor" />}
                  </button>
                  <div className="flex-1">
                    <div className="h-1.5 bg-navy/[0.1] rounded-full cursor-pointer overflow-hidden" onClick={handleSeek}>
                      <div className="h-full bg-gold rounded-full transition-all" style={{ width: duration ? ((currentTime / duration) * 100) + '%' : '0%' }} />
                    </div>
                    <div className="flex justify-between mt-1.5 text-[11px] text-navy/30">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                  <Volume2 size={16} className="text-navy/20 hidden sm:block" />
                </div>
              </div>

              <div className="flex items-center gap-4 mt-4">
                <button onClick={() => setShowShareModal(true)} className="flex items-center gap-2 px-4 py-2 bg-navy/[0.05] hover:bg-navy/[0.08] rounded-lg transition-colors text-xs text-navy/45 font-medium" data-testid="share-podcast-btn">
                  <Share2 size={14} /> Share
                </button>
                <Link to="/promond-publishing#videos" className="flex items-center gap-2 text-navy/35 hover:text-gold text-xs transition-colors">
                  <Play size={14} /> Book Trailer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Share Modal — kept dark: modal overlay on light pages looks intentional */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowShareModal(false)} />
          <div className="relative bg-navy-light border border-gold/10 rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="share-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-cream">Share Podcast</h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 rounded-lg hover:bg-cream/5 transition-colors">
                <X size={18} className="text-cream/40" />
              </button>
            </div>
            <div className="space-y-2">
              {[
                { action: copyLink, icon: <LinkIcon size={16} />, label: linkCopied ? 'Copied!' : 'Copy Link', sub: 'Share via any platform', bg: 'bg-gold/20' },
                { action: shareViaEmail, icon: <Mail size={16} />, label: 'Email', sub: 'Share via email', bg: 'bg-cream/10' },
                { action: shareOnLinkedIn, icon: <Linkedin size={16} />, label: 'LinkedIn', sub: 'Share with your network', bg: 'bg-[#0A66C2]/20' },
                { action: shareOnTwitter, icon: <Twitter size={16} />, label: 'X (Twitter)', sub: 'Tweet about it', bg: 'bg-cream/10' },
              ].map((s) => (
                <button key={s.label} onClick={s.action} className="flex items-center gap-3 w-full p-3 bg-cream/[0.03] hover:bg-cream/[0.07] rounded-lg transition-colors text-left">
                  <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center text-gold`}>{s.icon}</div>
                  <div>
                    <span className="text-cream/80 text-sm font-medium block">{s.label}</span>
                    <span className="text-cream/25 text-xs">{s.sub}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MINI COURSE ═══ */}
      <section className="bg-[#EDE9E0] py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 font-sans mb-4">Self-Paced Learning</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy font-semibold mb-3">Cultural Fluency Mini-Course</h2>
            <p className="text-navy/45 text-sm max-w-lg mx-auto">
              Take modules in any order. Access instructions sent via email after payment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="modules-grid">
            {modules.map((m) => {
              const hasAccess = m.isFree || hasPremiumAccess;
              return (
                <div key={m.id} className={`bg-white border rounded-xl p-6 transition-all duration-300 shadow-sm shadow-navy/[0.03] ${hasAccess ? 'border-green-500/20 hover:border-green-500/30' : 'border-navy/[0.07] hover:border-gold/25'}`} data-testid={`module-card-${m.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      {hasAccess ? <CheckCircle size={18} className="text-green-500 flex-shrink-0" /> : <BookOpen size={18} className="text-gold/60 flex-shrink-0" />}
                      <h3 className="font-serif text-lg text-navy font-semibold">Module {m.id}</h3>
                    </div>
                    {m.isFree && <span className="px-2.5 py-0.5 bg-green-500/10 text-green-600 rounded-full text-[10px] font-semibold uppercase tracking-wider">Free</span>}
                    {!m.isFree && hasPremiumAccess && <span className="px-2.5 py-0.5 bg-gold/10 text-gold/80 rounded-full text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"><Unlock size={10} />Unlocked</span>}
                  </div>
                  <p className="text-navy/65 text-sm font-medium mb-1">{m.title}</p>
                  <p className="text-navy/40 text-sm leading-relaxed mb-4">{m.desc}</p>
                  <div className="flex items-center gap-3 text-xs text-navy/30 mb-4">
                    <span>{m.duration}</span>
                    <span className="w-1 h-1 rounded-full bg-navy/15" />
                    <span>{m.lessons} lessons</span>
                  </div>

                  {hasAccess ? (
                    <div>
                      {m.isFree && !isModule1Open ? (
                        <button onClick={() => setIsModule1Open(true)} className="w-full px-4 py-2.5 bg-green-500/10 text-green-700 font-medium rounded-lg hover:bg-green-500/15 transition-colors text-sm" data-testid={`start-module-${m.id}`}>
                          Start Free Module
                        </button>
                      ) : (
                        <Link to={`/module-${m.id}`} className="inline-flex items-center gap-2 text-gold text-sm font-medium hover:text-gold-light transition-colors" data-testid={`continue-module-${m.id}`}>
                          Continue to Module {m.id} <ArrowRight size={14} />
                        </Link>
                      )}
                    </div>
                  ) : (
                    <a href="https://buy.stripe.com/8x2bJ03Pb4uT6t7exZ8so02" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gold/10 border border-gold/20 text-gold font-medium rounded-lg hover:bg-gold/20 transition-colors text-sm" data-testid={`unlock-module-${m.id}`}>
                      Unlock Module ({'\u00A3'}10) <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {hasPremiumAccess && (
            <div className="mt-8 p-4 bg-gold/[0.07] border border-gold/15 rounded-xl text-center">
              <div className="flex items-center justify-center gap-2 text-gold text-sm mb-1"><Unlock size={16} /><span className="font-semibold">Premium Access Active</span></div>
              <p className="text-navy/35 text-xs">You have access to all modules.</p>
            </div>
          )}
          {!hasPremiumAccess && (
            <p className="mt-6 text-center text-navy/30 text-xs">Each module: {'\u00A3'}10 (50% off {'\u00A3'}20)</p>
          )}
        </div>
        <div className="mt-20 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ═══ BOOK ENQUIRY ═══ */}
      <section id="book-enquiry" className="bg-cream py-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 font-sans mb-4">Get In Touch</p>
            <h3 className="font-serif text-2xl text-navy font-semibold mb-2">Book Enquiry</h3>
            <p className="text-navy/40 text-sm">Bulk orders, licensing, or questions about the book.</p>
          </div>

          <div className="bg-white border border-gold/15 rounded-xl p-6 sm:p-8 shadow-sm shadow-navy/[0.04]">
            {bookEnquiryStatus === 'success' ? (
              <div className="text-center py-6" data-testid="book-enquiry-success-message">
                <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-green-500" />
                </div>
                <h4 className="font-semibold text-navy text-lg mb-2">Enquiry Sent</h4>
                <p className="text-navy/45 text-sm mb-4">Confirmation sent. We'll respond within 2 business days.</p>
                <button onClick={() => { setBookEnquiryStatus('idle'); setBookEnquiry({ name: '', email: '', phone: '', message: '' }); }} className="text-gold hover:text-gold-light font-medium text-sm transition-colors">
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleBookEnquirySubmit} data-testid="book-enquiry-form">
                {bookEnquiryStatus === 'error' && (
                  <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6" data-testid="book-enquiry-error-message">
                    <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                    <p className="text-red-600 text-sm">{bookEnquiryError}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[10px] font-medium text-navy/35 mb-1.5 uppercase tracking-wider">Name *</label>
                    <input type="text" name="name" value={bookEnquiry.name} onChange={handleBookEnquiryChange} required disabled={bookEnquiryStatus === 'loading'} data-testid="book-enquiry-name-input" className="w-full bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/25 focus:outline-none focus:ring-1 focus:ring-gold/40 focus:border-gold/30 transition-all disabled:opacity-50" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-navy/35 mb-1.5 uppercase tracking-wider">Email *</label>
                    <input type="email" name="email" value={bookEnquiry.email} onChange={handleBookEnquiryChange} required disabled={bookEnquiryStatus === 'loading'} data-testid="book-enquiry-email-input" className="w-full bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/25 focus:outline-none focus:ring-1 focus:ring-gold/40 focus:border-gold/30 transition-all disabled:opacity-50" placeholder="you@example.com" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-[10px] font-medium text-navy/35 mb-1.5 uppercase tracking-wider">Phone (optional)</label>
                  <input type="tel" name="phone" value={bookEnquiry.phone} onChange={handleBookEnquiryChange} disabled={bookEnquiryStatus === 'loading'} data-testid="book-enquiry-phone-input" className="w-full bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/25 focus:outline-none focus:ring-1 focus:ring-gold/40 focus:border-gold/30 transition-all disabled:opacity-50" placeholder="+44 7000 000000" />
                </div>
                <div className="mb-6">
                  <label className="block text-[10px] font-medium text-navy/35 mb-1.5 uppercase tracking-wider">Message *</label>
                  <textarea name="message" value={bookEnquiry.message} onChange={handleBookEnquiryChange} required disabled={bookEnquiryStatus === 'loading'} rows="3" data-testid="book-enquiry-message-input" className="w-full bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-sm text-navy placeholder-navy/25 focus:outline-none focus:ring-1 focus:ring-gold/40 focus:border-gold/30 transition-all resize-none disabled:opacity-50" placeholder="Tell us about your enquiry..." />
                </div>
                <button type="submit" disabled={bookEnquiryStatus === 'loading'} data-testid="book-enquiry-submit-button" className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm font-semibold hover:bg-gold-light transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-gold/20">
                  {bookEnquiryStatus === 'loading' ? <><Loader2 size={14} className="animate-spin" /><span>Sending...</span></> : <><Send size={14} /><span>Send Enquiry</span></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ═══ PROMOND CTA ═══ */}
      <section className="bg-[#EDE9E0] py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold/60 font-sans mb-4">Published By</p>
          <h3 className="font-serif text-2xl text-navy font-semibold mb-3">Promond Publishing</h3>
          <p className="text-navy/40 text-sm mb-6 max-w-md mx-auto">More books, trailers, and press materials.</p>
          <Link to="/promond-publishing" className="inline-flex items-center gap-2 px-6 py-3 border border-navy/15 text-navy/60 font-sans text-sm font-medium rounded-full hover:border-gold/40 hover:text-gold transition-all duration-300">
            Visit Promond Publishing <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Books;
